import type { ContractStatus, MemberContract } from '@/data/seedMember'

/**
 * Availability rule for a contract's certificate ("attestation") in the
 * connected space (SI-1458).
 *
 * The certificate attests to a cover that is currently running, so once the
 * contract is over it must no longer be downloadable from the member's space.
 * The back office keeps its own link on a terminated contract — it is a separate
 * application and is deliberately untouched by this rule; the restriction is a
 * front-end display rule, not a backend one, and `GET
 * /contracts/:id/generate_certificate` still answers.
 *
 * Kept in `config/` next to `productCoverage.ts` because it is the same kind of
 * object: a business rule expressed as data, meant to be adjusted by editing a
 * list rather than by rewriting a condition spread across views.
 */

/**
 * View-model statuses that withdraw access to the certificate.
 *
 * Only `terminated` today, and it covers both cases named by the ticket: the
 * mapper folds the raw `terminated` (résilié) *and* `completed` (terminé)
 * statuses into this single view-model status — see `STATUS_MAP` in
 * `mappers/contract.mapper.ts`.
 *
 * `terminating` and `retracting` are deliberately absent: a termination or
 * withdrawal that has been *requested* leaves the cover running until its
 * effective date, and that is exactly when a member is most likely to need the
 * attestation (new insurer, school, landlord). Adding a status here is a
 * one-line change and automatically applies to every surface.
 */
export const CERTIFICATE_BLOCKED_STATUSES: ReadonlySet<ContractStatus> = new Set<ContractStatus>([
  'terminated',
])

/**
 * Whether the certificate of a contract may be downloaded from the connected
 * space.
 *
 * Takes only the status rather than the whole view-model so call sites can pass
 * anything contract-shaped, and nullish input answers `false` — callers often
 * hold a contract that may not be resolved yet (see
 * `certificateContractForProduct`) and should not have to guard first.
 */
export function canDownloadCertificate(
  contract: Pick<MemberContract, 'status'> | null | undefined,
): boolean {
  if (!contract) return false
  return !CERTIFICATE_BLOCKED_STATUSES.has(contract.status)
}
